import React, { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { connect } from "react-redux";

import {
  ColDef,
  ColGroupDef,
  ColumnApi,
  ColumnState,
  GridApi,
  GridReadyEvent,
  IColumnToolPanel,
} from "@ag-grid-community/core";
import { GetMainMenuItemsParams } from "@ag-grid-community/core/dist/cjs/entities/gridOptions";
import { AgGridReact } from "@ag-grid-community/react";
import { AgGridReactProps } from "@ag-grid-community/react/lib/agGridReact";
import { LicenseManager } from "@ag-grid-enterprise/core";
import cn from "classnames";
import { useIntl } from "react-intl";

import { LoadingSpinner } from "@mapmycustomers/ui";

import Theme from "@app/enum/Theme";
import { RootState } from "@app/store/rootReducer";
import { getTheme } from "@app/store/theme";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import defaultIdGetter from "@app/util/defaultIdGetter";

import styles from "./BaseGrid.module.scss";
import BaseHeader from "./gridComponents/headerCellRenderers/BaseHeader";
import NoRows from "./gridComponents/overlays/NoRows";
import baseColumnTypes from "./util/column-types/base-column-types";
import useColumnModelChangeListeners from "./util/hooks/useColumnModelChangeHandlers";
import useLocaleTextFunc from "./util/hooks/useLocaleTextFunc";

LicenseManager.setLicenseKey(process.env.REACT_APP_AGGRID_LICENSE_KEY as string);

// looks like this is a default row height for our styles
// AgGrid's default is 25 for balham theme, 42 for alpine
export const BASE_GRID_ROW_HEIGHT = 42;

export interface BaseGridMethods {
  getColumnApi: () => ColumnApi | undefined;
  getGridApi: () => GridApi | undefined;
}

// Default framework components for ag-grid
const defaultFrameworkComponents = {
  defaultHeader: BaseHeader,
  loadingComponent: LoadingSpinner,
  noRowsComponent: NoRows,
} as const;

// default column definition
const defaultColumnDef = {
  headerComponent: "defaultHeader",
};

export interface BaseGridProps extends AgGridReactProps {
  className?: string;
  columnToolPanelColumns?: (ColDef | ColGroupDef)[];
  onColumnModelChange?: (columnState: ColumnState[]) => void;
  showNoRows?: boolean;
}

interface Props extends BaseGridProps {
  theme: Theme;
}

const BaseGrid = memo(
  forwardRef<BaseGridMethods, Props>(
    (
      {
        className,
        columnToolPanelColumns,
        frameworkComponents,
        getMainMenuItems,
        loadingOverlayComponent,
        noRowsOverlayComponent,
        onColumnModelChange,
        onColumnPinned,
        onColumnResized,
        onColumnVisible,
        onDragStopped,
        onGridReady,
        paginationPageSize = DEFAULT_PAGE_SIZE,
        showNoRows,
        theme,
        ...otherGridProps
      }: Props,
      ref
    ) => {
      const intl = useIntl();

      const gridApi = useRef<GridApi>();
      const columnApi = useRef<ColumnApi>();

      const updateColumnToolPanel = useCallback(() => {
        if (columnToolPanelColumns && gridApi.current) {
          const columnsToolPanel = gridApi.current.getToolPanelInstance(
            "columns"
          ) as unknown as IColumnToolPanel;
          if (columnsToolPanel) {
            columnsToolPanel.setColumnLayout(columnToolPanelColumns);
          }
        }
      }, [columnToolPanelColumns]);

      const handleGridReady = useCallback(
        (event: GridReadyEvent) => {
          gridApi.current = event.api;
          columnApi.current = event.columnApi;
          onGridReady?.(event);
        },
        [onGridReady]
      );

      useImperativeHandle(ref, () => ({
        getColumnApi: () => columnApi.current,
        getGridApi: () => gridApi.current,
      }));

      // support grid localization
      const translate = useLocaleTextFunc();

      // support grid model change event
      const columnChangeHandlers = useColumnModelChangeListeners({
        onColumnModelChange,
        onColumnPinned,
        onColumnResized,
        onColumnVisible,
        onDragStopped,
        syncColumnToolPanel: updateColumnToolPanel,
      });

      const finalFrameworkComponents = useMemo(
        () => ({ ...defaultFrameworkComponents, ...frameworkComponents }),
        [frameworkComponents]
      );

      updateColumnToolPanel();

      if (gridApi.current) {
        if (showNoRows) {
          // setTimeout is needed to ensure that the latest noRowsOverlayComponentParams
          // props are applied before calling showNoRowsOverlay
          setTimeout(() => gridApi.current?.showNoRowsOverlay(), 0);
        } else {
          gridApi.current.hideOverlay();
        }
      }

      const handleGetMainMenuItems = useCallback((params: GetMainMenuItemsParams) => {
        // remove Reset Column menu item as it has no use currently
        const result = params.defaultItems.filter((item) => item !== "resetColumns");
        while (result.at(-1) === "separator") {
          result.pop();
        }
        return result;
      }, []);

      return (
        <section
          className={cn(styles.container, className, {
            "ag-theme-alpine": theme === Theme.LIGHT,
            "ag-theme-alpine-dark": theme === Theme.DARK,
          })}
          style={{
            "--mmc-grid-columns-panel-title": `"${intl.formatMessage({
              id: "grid.sidebar.columnsPanel.title",
              defaultMessage: "Select Columns to View",
              description: "Title in grid's columns sidebar",
            })}"`,
            "--mmc-grid-filter-panel-title": `"${intl.formatMessage({
              id: "grid.sidebar.filterPanel.title",
              defaultMessage: "Select Advanced Filters",
              description: "Title in grid's filter sidebar",
            })}"`,
            "--mmc-grid-manage-label": `"${intl.formatMessage({
              id: "grid.sidebar.manage.label",
              defaultMessage: "Manage",
              description: "Manage label in grid's sidebar",
            })}"`,
          }}
        >
          <AgGridReact
            allowDragFromColumnsToolPanel
            // prevent loading data too frequently, not needed with pagination
            // but let's keep it here just in case
            blockLoadDebounceMillis={100}
            // return as many rows at a time as the page size is
            cacheBlockSize={paginationPageSize}
            // default column props for known FieldTypes and FieldFeatures
            columnTypes={baseColumnTypes}
            // enable debugging
            debug={!!process.env.REACT_APP_AGGRID_DEBUG}
            // default column definitions, applied to all columns
            defaultColDef={defaultColumnDef}
            // default framework components
            frameworkComponents={finalFrameworkComponents}
            // default menu items getter
            getMainMenuItems={getMainMenuItems ?? handleGetMainMenuItems}
            // default row id getter
            getRowNodeId={defaultIdGetter}
            // default overlay component for the loading state
            loadingOverlayComponent={loadingOverlayComponent ?? "loadingComponent"}
            // support ag-grid localization
            localeTextFunc={translate}
            // default overlay component for the no rows state
            noRowsOverlayComponent={noRowsOverlayComponent ?? "noRowsComponent"}
            // listen column pinned event to fire onColumnStateChanged
            onColumnPinned={columnChangeHandlers.handleColumnPinned}
            // listen column resized event to fire onColumnStateChanged
            onColumnResized={columnChangeHandlers.handleColumnResized}
            // listen column visible event to fire onColumnStateChanged
            onColumnVisible={columnChangeHandlers.handleColumnVisible}
            // listen column drag stopped event to fire onColumnStateChanged
            onDragStopped={columnChangeHandlers.handleDragStopped}
            // default grid ready to capture the grid and the column apis
            onGridReady={handleGridReady}
            // enable pagination by default
            pagination
            // pagination size
            paginationPageSize={paginationPageSize}
            // default row buffer, using 20 as an arbitrary value
            rowBuffer={20}
            // we don't need to select individual cells in most cases
            suppressCellSelection
            // we are fine with the horizontal scroll in most cases
            suppressHorizontalScroll={false}
            // we only allow selecting a row by checkbox click to let users select text
            // in cells to copy it for instance
            suppressRowClickSelection
            // now include other ag-grid props, including overrides for those specified above
            {...otherGridProps}
          />
        </section>
      );
    }
  )
);

export interface BaseGridComponent extends React.ComponentClass<Props>, BaseGridMethods {}

const mapStateToProps = (state: RootState) => ({
  theme: getTheme(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(
  BaseGrid
);
